import { Select as SelectPrimitive } from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "@fleet/icons";
import { createMemo, type JSX, Show, splitProps } from "solid-js";
import { cn, reconcileById } from "@stockpilot/shared";

import { overlayPortalHost } from "./overlay-portal";

interface SelectOption {
  value: string;
  /** Display label. `null`/`undefined` render as an empty string (parity with native <option>). */
  label: string | null | undefined;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true. */
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph. */
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No products yet"). */
  description?: string;
}

interface SelectProps {
  /** Currently selected value (controlled). */
  value: string;
  /** Called with the new value when the selection changes. */
  onChange: (value: string) => void;
  /** Flat list of options to render in the listbox. */
  options: SelectOption[];
  /** Placeholder rendered when no value is selected. */
  placeholder?: string;
  /** Accessible label for the trigger. */
  "aria-label"?: string;
  /** Extra classes for the trigger button. */
  class?: string;
  /** Extra classes for the listbox content panel. */
  contentClass?: string;
  disabled?: boolean;
  /** Forwarded to the trigger so existing e2e selectors keep working. */
  "data-testid"?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA. */
  emptyLabel?: string;
  /**
   * Forwarded to the hidden native `<select>` (see `HiddenSelect` below) as its
   * `name` attribute. Optional and unconditional: pass it on call-sites that
   * need this field to participate in browser autofill or a native form
   * submission, omit it otherwise. `HiddenSelect` itself is ALWAYS mounted
   * regardless of whether `name` is set — see the comment at its render site
   * for why gating the mount on `name` would silently defeat the fix.
   */
  name?: string;
}

/**
 * Premium Select built on `@kobalte/core` — replaces native `<select>` across
 * the app. Mirrors the native value/onChange contract (string value, string
 * callback) plus a flat `options` array so call-sites migrate mechanically.
 *
 * Theme-safe (accent / accent-foreground token pairs), animated panel,
 * full keyboard + typeahead + ARIA from Kobalte. Honours
 * `prefers-reduced-motion` via the global guard in global.css.
 */
export function Select(props: SelectProps): JSX.Element {
  const [local, others] = splitProps(props, [
    "value",
    "onChange",
    "options",
    "placeholder",
    "aria-label",
    "class",
    "contentClass",
    "disabled",
    "data-testid",
    "emptyAction",
    "emptyLabel",
    "name",
  ]);

  // ── CC-26: STABLE identity for the options (CC-23 recipe within Select itself).
  // Call-sites usually feed `options` with an inline `.map()`
  // over `query.data` — and LIVE PowerSync queries re-emit arrays and
  // objects with NEW identity on each tick even if nothing changes. Kobalte
  // rebuilds its collection from those identities and its Listbox
  // re-executes `renderItem` per node (`<Key by="key">` reuses the position
  // but the item node/JSX is recreated) → with the listbox OPEN, the DOM of
  // each option REMOUNTS under the pointer and the user click lands on a
  // stale node: the selection is discarded (empty value, submit disabled).
  // `reconcileById` collapses each re-emission without real changes to the previous
  // array/objects, so the collection is not rebuilt and the click persists.
  // Guarded by components/ui/__tests__/select-live-options.test.tsx.
  const stableOptions = createMemo<SelectOption[]>((prev) =>
    reconcileById(prev ?? [], local.options, (o) => o.value),
  );

  const selectedOption = () => stableOptions().find((o) => o.value === local.value) ?? null;
  const isEmpty = () => stableOptions().length === 0;

  return (
    <SelectPrimitive<SelectOption>
      options={stableOptions()}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      disabled={local.disabled}
      value={selectedOption()}
      // Single-select semantics: re-clicking the already-selected option must NOT
      // toggle it off. Kobalte's default lets a click on the current selection
      // emit `null` (deselect) — which here coerced the value back to "" and
      // silently cleared the field (e.g. re-picking the same product/location in
      // the stock-adjustment form wiped the selection before submit). This is a
      // required single-select, so disallow emptying it by interaction. The value
      // still starts empty (placeholder) and can always be CHANGED to another
      // option; it just can't be un-set by re-clicking.
      disallowEmptySelection
      onChange={(option) => {
        // With `disallowEmptySelection` Kobalte no longer emits null on a
        // re-click; keep the `?? ""` coercion as a defensive floor.
        local.onChange(option?.value ?? "");
      }}
      placeholder={local.placeholder}
      itemComponent={(itemProps) => (
        <SelectPrimitive.Item
          item={itemProps.item}
          class={cn(
            "relative flex cursor-pointer select-none items-center rounded-md py-2 pl-8 pr-3 text-sm outline-none transition-colors",
            "text-popover-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        >
          <SelectPrimitive.ItemIndicator class="absolute left-2 inline-flex items-center justify-center">
            <Check class="h-4 w-4" />
          </SelectPrimitive.ItemIndicator>
          <SelectPrimitive.ItemLabel>
            {itemProps.item.rawValue.label ?? ""}
          </SelectPrimitive.ItemLabel>
        </SelectPrimitive.Item>
      )}
    >
      {/* Native <select> that Kobalte can mount, visually hidden (out of
          flow, no click surface), with the real <option selected>
          reflecting the selection. This is Kobalte's OFFICIAL pattern:
          sibling of Trigger, mounted BEFORE. NOT a child of Trigger — Kobalte
          renders Trigger as <button>, and nesting a <select>/<option>
          inside a <button> violates HTML5 content model
          (interactive content inside interactive content).
          Browsers tolerate it so it doesn't look broken, but it is invalid and
          can bite with validators, screen readers or a future
          Kobalte change. A prior attempt nested it inside Trigger
          to "not introduce a wrapper changing the DOM of ~40
          call-sites" — but unnecessary: `HiddenSelect` already wraps
          itself in its own <div style="position:absolute; ...1px clip">
          (visually hidden, out of flow), so placing it as a sibling adds neither
          its own wrapper nor affects layout/flex of
          any existing call-site. Without this node Select exposed
          NO native <option> (the entire visible listbox is a Popover with
          ARIA divs/roles), breaking autofill, native form submission and
          any assert inspecting selection via native DOM.
          `name` is ALWAYS forwarded (not conditioned on caller passing it):
          if gated with <Show when={local.name}>, node does not mount unless
          a call-site passes `name` — and since none does yet, the
          fix would stay dead at runtime (seen in meal-planner-app). Mounting it
          always is free: without `name`, the hidden <select> simply lacks
          the attribute, but still exposes `<option selected>` for
          FEFO/autofill/screen readers.
          `data-testid` takes its own suffix (instead of reusing Trigger's)
          because it is now a sibling element, not a descendant of
          Trigger: the FEFO lot tests in `deeplink-overlays.test.tsx` need to target the native
          <select> directly to read its selected value. */}
      <SelectPrimitive.HiddenSelect
        name={local.name}
        data-testid={local["data-testid"] ? `${local["data-testid"]}-hidden-select` : undefined}
      />
      <SelectPrimitive.Trigger
        aria-label={local["aria-label"]}
        data-testid={local["data-testid"]}
        class={cn(
          "inline-flex h-10 items-center justify-between gap-2 rounded-md border border-border bg-field-background px-3 text-sm",
          "outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0",
          "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
          "hover:border-accent/60",
          local.class,
        )}
        {...others}
      >
        <SelectPrimitive.Value<SelectOption> class="block truncate text-left">
          {(state) => (
            <Show
              when={state.selectedOption()}
              fallback={<span class="text-muted-foreground">{local.placeholder}</span>}
            >
              {state.selectedOption()?.label ?? ""}
            </Show>
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 opacity-60">
          <ChevronsUpDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {/* mount: host tagged data-live-announcer so modal Dialog
          ariaHideOutside does NOT hide the listbox (CC-21) — see overlay-portal.ts. */}
      <SelectPrimitive.Portal mount={overlayPortalHost()}>
        <SelectPrimitive.Content
          class={cn(
            "ui-select-content z-[250] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none",
            local.contentClass,
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-72 overflow-y-auto p-1" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
